import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import api from "../api";
import FormattedInput from "../components/FormattedInput";

console.log("LancamentoIndicadores está renderizando!");

const LancamentoIndicadores = () => {
  const hoje = useMemo(() => new Date(), []);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(null);
  const [indicadores, setIndicadores] = useState([]);
  const [valores, setValores] = useState({});
  const [mensagem, setMensagem] = useState("");
  const [jaLancado, setJaLancado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null); // ID do usuário logado

  useEffect(() => {
    // Buscar perfil do usuário logado
    const fetchPerfil = async () => {
      try {
        const response = await api.get("/usuarios/me");
        console.log(response.data.perfil);
        setIsAdmin(response.data.perfil === "ADMINISTRADOR");
        setCurrentUserId(response.data.id); // Armazenar ID do próprio usuário
        if (response.data.perfil === "ADMINISTRADOR") {
          // Buscar lista de usuários se for administrador
          const responseUsuarios = await api.get("/usuarios");
          setUsuarios(responseUsuarios.data);
        }
      } catch (error) {
        console.error("Erro ao buscar perfil do usuário:", error);
        setMensagem("Erro ao carregar informações do usuário.");
      }
    };
    fetchPerfil();
  }, []);

  // Buscar indicadores e lançamentos existentes
  useEffect(() => {
    const fetchDados = async () => {
      if (ano !== null && mes !== null) {
        setLoading(true);
        try {
          // Buscar indicadores ativos
          const responseIndicadores = await api.get(`/indicadores/ativos`);
          setIndicadores(responseIndicadores.data);

          // Buscar lançamentos existentes
          const userId =
            isAdmin && selectedUsuario ? selectedUsuario : currentUserId;
          const responseLancamentos = await api.get(
            `/lancamentos/disponiveis/${ano}/${mes + 1}` +
              (userId ? `?usuarioId=${userId}` : "")
          );

          if (responseLancamentos.data.length > 0) {
            setJaLancado(true);
            // Mapear valores existentes para os indicadores
            const valoresExistentes = responseLancamentos.data.reduce(
              (acc, lancamento) => {
                acc[lancamento.indicadorId] = lancamento.valor;
                return acc;
              },
              {}
            );
            setValores(valoresExistentes);
          } else {
            setJaLancado(false);
            setValores({}); // Limpar valores se não houver lançamentos
          }
        } catch (error) {
          console.error("Erro ao buscar dados:", error);
          setMensagem(
            error.response?.data?.message ||
              "Erro ao buscar dados. Tente novamente."
          );
          setJaLancado(false);
          setValores({}); // Garantir que os valores sejam limpos em caso de erro
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDados();
  }, [ano, mes, isAdmin, selectedUsuario, currentUserId]);

  const anosPermitidos = useMemo(
    () =>
      Array.from(
        { length: hoje.getFullYear() - 2020 + 1 },
        (_, i) => hoje.getFullYear() - i
      ),
    [hoje]
  );

  const mesesPermitidos = useMemo(() => {
    if (ano === hoje.getFullYear()) {
      return Array.from({ length: hoje.getMonth() }, (_, i) => i);
    }
    return Array.from({ length: 12 }, (_, i) => i);
  }, [ano, hoje]);

  const handleValorChange = (id, valor) => {
    setValores((prev) => ({ ...prev, [id]: valor }));
  };

  const handleSubmit = async () => {
    const userId = isAdmin && selectedUsuario ? selectedUsuario : currentUserId;

    try {
      const lancamentos = indicadores.map((indicador) => ({
        indicadorId: indicador.id,
        ano,
        mes: mes + 1,
        valor: valores[indicador.id] || 0,
        userId: userId,
      }));

      await Promise.all(
        lancamentos.map((lancamento) => api.post("/lancamentos", lancamento))
      );
      setMensagem("Lançamentos realizados com sucesso.");
      setJaLancado(false);
    } catch (error) {
      console.error("Erro ao realizar lançamentos:", error);
      setMensagem("Erro ao realizar lançamentos. Tente novamente.");
    }
  };

  const isFormValid = indicadores.every(
    (indicador) =>
      valores[indicador.id] !== undefined &&
      valores[indicador.id] !== null &&
      valores[indicador.id] !== ""
  );

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h4"
        mb={3}
        sx={{ textAlign: "center", fontWeight: "bold" }}
      >
        Lançamento de Indicadores
      </Typography>

      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "0 auto" }} />
      ) : (
        <>
          {isAdmin && (
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="usuario-label">Usuário</InputLabel>
              <Select
                labelId="usuario-label"
                value={selectedUsuario || ""}
                onChange={(e) => setSelectedUsuario(e.target.value)}
                label="Usuário"
              >
                {usuarios.map((usuario) => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="ano-label">Ano</InputLabel>
            <Select
              labelId="ano-label"
              value={ano}
              onChange={(e) => {
                setAno(e.target.value);
                setMes(null);
                setValores({}); // Limpar valores ao mudar ano
              }}
              label="Ano"
            >
              {anosPermitidos.map((anoPermitido) => (
                <MenuItem key={anoPermitido} value={anoPermitido}>
                  {anoPermitido}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="mes-label">Mês</InputLabel>
            <Select
              labelId="mes-label"
              value={mes}
              onChange={(e) => {
                setMes(e.target.value);
                setValores({}); // Limpar valores ao mudar mês
              }}
              label="Mês"
            >
              {mesesPermitidos.map((mesPermitido) => (
                <MenuItem key={mesPermitido} value={mesPermitido}>
                  {new Date(0, mesPermitido).toLocaleString("pt-BR", {
                    month: "long",
                  })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {jaLancado ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Já existe lançamento neste período.
              </Alert>
              {isAdmin && (
                <>
                  {indicadores.map((indicador) => (
                    <Box key={indicador.id} sx={{ mb: 2 }}>
                      <Typography>
                        {indicador.descricao} ({indicador.unidade.nome})
                      </Typography>
                      <FormattedInput
                        tipo={
                          indicador.unidade.tipoValor === "inteiro"
                            ? "inteiro"
                            : "decimal"
                        }
                        value={valores[indicador.id] || ""}
                        onChange={(novoValor) =>
                          handleValorChange(indicador.id, novoValor)
                        }
                        label={`Valor para ${indicador.descricao}`}
                      />
                    </Box>
                  ))}
                </>
              )}
            </>
          ) : (
            indicadores.map((indicador) => (
              <Box key={indicador.id} sx={{ mb: 2 }}>
                <Typography>
                  {indicador.descricao} ({indicador.unidade.nome})
                </Typography>
                <FormattedInput
                  tipo={
                    indicador.unidade.tipoValor === "inteiro"
                      ? "inteiro"
                      : "decimal"
                  }
                  value={valores[indicador.id] || ""}
                  onChange={(novoValor) =>
                    handleValorChange(indicador.id, novoValor)
                  }
                  label={`Valor para ${indicador.descricao}`}
                />
              </Box>
            ))
          )}

          {/* 🔥 Corrigido: O botão agora sempre aparece */}
          {(!jaLancado || isAdmin) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!ano || mes === null || !isFormValid}
            >
              Salvar Lançamentos
            </Button>
          )}

          {mensagem && (
            <Alert
              severity={mensagem.includes("sucesso") ? "success" : "error"}
              sx={{ mt: 2 }}
            >
              {mensagem}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default LancamentoIndicadores;
